import produce from 'immer';
import {useEffect, useMemo, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {
  CheckEntryResponseState,
  CheckEntryResultUsefulState,
  DisplayCheckEntryResults,
} from 'notebook/ai-tools/NotebookAIToolsConstants';
import {checkEntryUsingAI, CheckEntryUsingAIResponse} from 'notebook/ai-tools/NotebookAIToolsDAO';
import {
  getTablesSubmissionStates,
  StructuredTableSubmissionStatus,
} from 'notebook/reviews/disableAuthorActions';
import entryStore from 'stores/EntryStore';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

interface CheckEntryState {
  // Network request state.
  isLoading: boolean;
  isError: boolean;
  response: CheckEntryUsingAIResponse | null;
  // UI state
  loadingState: string | null;
  completedStates: {[index: number]: boolean | undefined};
  usefulStates: {[index: number]: CheckEntryResultUsefulState};
}

const LOADING_BLURBS = [
  'Requesting Notebook Check…',
  'Reading entry content…',
  'Comparing against guidelines…',
  'Preparing results…',
];

const MEDIAN_LOADING_TIME_MS = 12_000;

const DEFAULT_STATE: CheckEntryState = {
  isLoading: false,
  isError: false,
  response: null,
  loadingState: null,
  completedStates: {},
  usefulStates: {},
};

const checkEntryResponseState = makeUseGlobalClientState<CheckEntryState>(DEFAULT_STATE);

interface CheckEntryResultInfo {
  checkEntryResults: DisplayCheckEntryResults;
  onToggleCompleted: (index: number) => void;
  onToggleUsefulState: (index: number, value: CheckEntryResultUsefulState) => void;
  onRecompute: () => Promise<void>;
}

/**
 * Load AI Notebook Checker results for this entry and manage user-facing state.
 *
 * The entry is checked on initial mount of the component (i.e. when the review tab is opened directly
 * or when the user clicks a button to check the entry), and on explicit refresh from the user, and
 * kept stable aside from those events. In the future we may want to explore more continuous checking,
 * but this approach allows the user to use the results like a checklist.
 */
export default function useCheckEntryResultInfo(entryId: string): CheckEntryResultInfo {
  const [loadingBlurbIndex, setLoadingBlurbIndex] = useState(0);
  const [checkEntryState, setCheckEntryState] = checkEntryResponseState.useGlobalClientState(entryId);
  const needsInitialLoad =
    !checkEntryState.isLoading && !checkEntryState.isError && !checkEntryState.response;
  // Extra safeguard to avoid a fetch loop: only run the initial load once. This is already implied
  // by needsInitialLoad, but easier to manually confirm.
  const hasLoaded = useRef(false);

  useEffect(() => {
    (async () => {
      if (needsInitialLoad && !hasLoaded.current) {
        hasLoaded.current = true;
        await checkEntry();
      }
    })();
  });

  useEffect(() => {
    // update the loading state with the current loading blurb
    if (checkEntryState.isLoading && checkEntryState.loadingState !== LOADING_BLURBS[loadingBlurbIndex]) {
      setCheckEntryState(
        produce(checkEntryState, (draft) => {
          draft.loadingState = LOADING_BLURBS[loadingBlurbIndex];
        })
      );
    }
  }, [loadingBlurbIndex, setCheckEntryState, checkEntryState]);

  async function checkEntry(): Promise<void> {
    let response;
    setLoadingBlurbIndex(0);
    setCheckEntryState({...DEFAULT_STATE, isLoading: true, loadingState: LOADING_BLURBS[0]});

    // Collect structured table submission statuses if the entry exists
    let structuredTableStatuses: Array<StructuredTableSubmissionStatus> = [];
    if (entryStore.hasEntry(entryId)) {
      const entry = entryStore.getEntry(entryId);
      structuredTableStatuses = getTablesSubmissionStates(entry!.note);
    }

    // Fake loading progress by cycling through loading blurbs
    // If it takes longer than the median loading time, we will end up sitting on the last blurb for awhile
    const int = setInterval(
      () => {
        setLoadingBlurbIndex((prev) => Math.min(prev + 1, LOADING_BLURBS.length - 1));
      },
      Math.floor(MEDIAN_LOADING_TIME_MS / LOADING_BLURBS.length)
    );

    try {
      response = await checkEntryUsingAI(entryId, structuredTableStatuses);
    } catch (e) {
      clearInterval(int);
      setCheckEntryState({...DEFAULT_STATE, isError: true});
      throw e;
    }
    clearInterval(int);
    setCheckEntryState({...DEFAULT_STATE, response});
  }

  function onToggleCompleted(index: number): void {
    const newValue = !checkEntryState.completedStates[index];
    trackEvent(ELN_ANALYTICS_ACTIONS.MARKED_AI_NOTEBOOK_CHECK_RESULT_COMPLETED, {
      traceId: checkEntryState.response?.traceId,
      checkResultIndex: index,
      entryId,
      newValue,
    });
    // Note: this can be a little fragile after if we somehow have an old callback run after re-fetch.
    // Since we'll switch to loading and back on re-fetch, it seems relatively safe to assume that
    // won't happen, but this could be made more robust by letting setCheckEntryState take an updater
    // function (and refusing if traceId has changed) or storing state keyed off of traceId.
    setCheckEntryState(
      produce(checkEntryState, (draft) => {
        draft.completedStates[index] = newValue;
      })
    );
  }

  function onToggleUsefulState(index: number, value: CheckEntryResultUsefulState): void {
    const newValue =
      value === checkEntryState.usefulStates[index] ? CheckEntryResultUsefulState.UNMARKED : value;
    trackEvent(ELN_ANALYTICS_ACTIONS.MARKED_AI_NOTEBOOK_CHECK_RESULT_USEFUL, {
      traceId: checkEntryState.response?.traceId,
      checkResultIndex: index,
      entryId,
      newValue,
    });
    setCheckEntryState(
      produce(checkEntryState, (draft) => {
        draft.usefulStates[index] = newValue;
      })
    );
  }

  const checkEntryResults = useMemo(
    () => formatCheckEntryResultsForDisplay(checkEntryState),
    [checkEntryState]
  );

  return {
    checkEntryResults,
    onRecompute: checkEntry,
    onToggleCompleted,
    onToggleUsefulState,
  };
}

/**
 * Given the raw normalized state of check entry results, prepare it into a format that's easier to send
 * down as React props.
 */
function formatCheckEntryResultsForDisplay(checkEntryState: CheckEntryState): DisplayCheckEntryResults {
  if (checkEntryState.isLoading) {
    return {
      state: CheckEntryResponseState.LOADING,
      loadingState: checkEntryState.loadingState ?? LOADING_BLURBS[0],
    };
  }
  if (checkEntryState.response && !checkEntryState.response.hadError) {
    return {
      state: CheckEntryResponseState.SUCCESS,
      traceId: checkEntryState.response.traceId,
      results: checkEntryState.response.checkResults.map((checkResult, i) => ({
        text: checkResult.text,
        isCompleted: Boolean(checkEntryState.completedStates[i]),
        usefulState: checkEntryState.usefulStates[i] ?? CheckEntryResultUsefulState.UNMARKED,
      })),
      checkedAt: checkEntryState.response.checkedAt as string,
      checkedVersion: checkEntryState.response.checkedVersion as number,
    };
  }
  if (checkEntryState.isError || checkEntryState.response?.hadError) {
    return {
      state: CheckEntryResponseState.ERROR,
      errorMessage: checkEntryState.response?.errorMessage ?? null,
    };
  }
  // We might be in a completely initial state (i.e. will load soon) or in an explicit loading state,
  // and in any case, show loading to the user.
  return {state: CheckEntryResponseState.LOADING, loadingState: LOADING_BLURBS[0]};
}
